import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { extractErrorMessage } from '../../core/services/api';
import {
  llmAgent,
  resources,
  type LlmJobState,
  type LlmJobStatus,
} from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import type { Item, Project } from '../../core/types/data-models';

/**
 * Shape of `result` returned by `generateItemsAction`. Mirrors the
 * "Output format" stanza in `generate-items.prompt.md` — a list of
 * suggested items with a name and a single-sentence description.
 */
interface ItemSuggestion {
  name: string;
  description?: string;
}

interface ItemsResult {
  items?: ItemSuggestion[];
}

interface GenerateItemsDialogProps {
  open: boolean;
  /** Initial project to generate items for. The user can change it. */
  defaultProjectId?: number | null;
  onClose: () => void;
}

const TERMINAL_STATES: ReadonlySet<LlmJobState> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

/**
 * Dialog that drives the "Generate items" flow:
 *
 *   1. Collect parameters (project, count, optional hint).
 *   2. POST /items/generate-items/:projectId to start the LLM job.
 *   3. Poll GET /llm-agent/jobs/:jobId every 1.5s until terminal.
 *   4. Render the result with one checkbox per suggested item; the
 *      user picks which to save and clicks "Save selected".
 *   5. For each picked suggestion, create one `Item` row.
 */
export function GenerateItemsDialog({
  open,
  defaultProjectId,
  onClose,
}: GenerateItemsDialogProps) {
  const { activeTenantId } = useTenant();
  const queryClient = useQueryClient();

  // ----- form params (collected before the job starts) ---------------
  const [projectId, setProjectId] = useState<number | null>(
    defaultProjectId ?? null,
  );
  const [count, setCount] = useState<number>(5);
  const [hint, setHint] = useState<string>('');

  // ----- job state ---------------------------------------------------
  const [status, setStatus] = useState<LlmJobStatus<ItemsResult> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const jobIdRef = useRef<string | null>(null);

  // ----- start job ----------------------------------------------------
  const start = useMutation({
    mutationFn: (input: { projectId: number; count: number; hint: string }) =>
      resources.generateItems(input.projectId, {
        count: input.count,
        hint: input.hint || undefined,
      }),
    onSuccess: (data) => {
      jobIdRef.current = data.jobId;
      setStatus(data.status as LlmJobStatus<ItemsResult>);
    },
    onError: (err) => setStartError(extractErrorMessage(err)),
  });

  // ----- save selection ----------------------------------------------
  const save = useMutation({
    mutationFn: async (picks: ItemSuggestion[]) => {
      if (projectId == null) return;
      for (const pick of picks) {
        const name = pick.name?.trim();
        if (!name) continue;
        await resources.items.create({
          projectId,
          name,
          description: pick.description?.trim() || null,
          ...(activeTenantId != null ? { tenant_id: activeTenantId } : {}),
        } as Partial<Item>);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const resetJob = () => {
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
  };

  const retry = () => {
    if (projectId == null) return;
    resetJob();
    start.mutate({ projectId, count, hint });
  };

  // Reset whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    resetJob();
    setProjectId(defaultProjectId ?? null);
    setCount(5);
    setHint('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ----- polling loop -------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (status == null) return;
    if (TERMINAL_STATES.has(status.state)) return;
    let cancelled = false;
    const tick = async () => {
      const jobId = jobIdRef.current;
      if (!jobId) return;
      try {
        const next = await llmAgent.getJob<ItemsResult>(jobId);
        if (!cancelled) setStatus(next);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    };
    const handle = window.setInterval(tick, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, status]);

  // Pre-select every suggestion once the job completes so "Save selected"
  // is one click for the common "looks good" case.
  useEffect(() => {
    if (status?.state !== 'completed') return;
    const items = status.result?.items ?? [];
    setSelected(new Set(items.map((_, idx) => idx)));
  }, [status]);

  const items = status?.result?.items ?? [];
  const lastStepLabel =
    status?.steps?.[status.steps.length - 1]?.step ??
    status?.steps?.[status.steps.length - 1]?.name;

  const toggle = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const hasJob = status != null || start.isPending;
  const isCompleted = status?.state === 'completed';
  const isRunning =
    start.isPending || (status != null && !TERMINAL_STATES.has(status.state));
  const canStart = projectId != null && !start.isPending;
  const canSave = isCompleted && selected.size > 0 && !save.isPending;

  const handleStart = () => {
    if (!canStart) return;
    resetJob();
    start.mutate({ projectId: projectId!, count, hint });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // While the job is running or not started yet, the submit button
    // doubles as "Generate"; once results are in, it submits the
    // chosen suggestions.
    if (!hasJob) {
      handleStart();
      return;
    }
    if (!canSave) return;
    setError(null);
    const picks = items.filter((_, idx) => selected.has(idx));
    await save.mutateAsync(picks);
  };

  return (
    <FormDialog
      open={open}
      title="Generate items"
      submitLabel={
        save.isPending
          ? 'Saving…'
          : isCompleted
            ? 'Save selected'
            : 'Generate'
      }
      loading={save.isPending}
      submitDisabled={!hasJob ? !canStart : !canSave}
      maxWidth="md"
      onSubmit={handleSubmit}
      onClose={() => {
        if (save.isPending) return;
        onClose();
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      {/* ---- Parameters (always visible — user can tweak and retry) ---- */}
      <Stack spacing={2}>
        <EntitySelect<Project>
          label="Project"
          required
          helperText="Items will be generated for this project. Existing items are passed to the model so it doesn't propose duplicates."
          cacheKey={['projects', 'select']}
          service={resources.projects}
          value={projectId}
          onChange={(id) => setProjectId(id ?? null)}
        />
        <Stack direction="row" spacing={2}>
          <TextField
            select
            label="How many"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            sx={{ minWidth: 120 }}
            disabled={isRunning}
          >
            {[3, 5, 8, 10, 15, 20].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Hint (optional)"
            fullWidth
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="Anything that should steer the generation"
            disabled={isRunning}
          />
        </Stack>
      </Stack>

      {/* ---- Job progress ---- */}
      {isRunning && (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {status == null
                ? 'Starting…'
                : lastStepLabel ?? 'Generating items…'}
            </Typography>
          </Stack>
          <LinearProgress
            variant={
              status?.progress != null && status.progress > 0
                ? 'determinate'
                : 'indeterminate'
            }
            value={
              status?.progress != null
                ? Math.round(status.progress * 100)
                : undefined
            }
          />
        </Stack>
      )}

      {status?.state === 'failed' && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {status.error || 'The generate-items job failed.'}
        </Alert>
      )}
      {status?.state === 'cancelled' && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          The generate-items job was cancelled.
        </Alert>
      )}
      {startError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {startError}
        </Alert>
      )}

      {/* ---- Results review ---- */}
      {isCompleted && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {items.length === 0 ? (
            <Alert severity="warning">The model returned no items.</Alert>
          ) : (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Pick the suggestions to insert into the items table. They are
                pre-selected — uncheck the ones you don't want.
              </Typography>
              {items.map((it, idx) => (
                <Paper
                  key={`${it.name}-${idx}`}
                  variant="outlined"
                  sx={{ p: 1.5 }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Checkbox
                      checked={selected.has(idx)}
                      onChange={() => toggle(idx)}
                      sx={{ p: 0.5, mt: -0.25 }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {it.name}
                      </Typography>
                      {it.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {it.description}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </FormDialog>
  );
}
